import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { generateClient } from "aws-amplify/data";
import { listSets, wordsBySetName } from "../../graphql/queries"; // Adjust the path as needed
import generateSignedURL from "../../utils/generateSignedURL"; // Adjust the path as needed

const client = generateClient();

// Async thunk to fetch sets
const fetchSets = createAsyncThunk("sets/fetchSets", async () => {
  try {
    // Execute the GraphQL query to fetch all sets
    const response = await client.graphql({
      query: listSets,
    });

    // Return the fetched items and the next token for pagination
    return {
      sets: response.data.listSets?.items || [], // Ensure items is an array
    };
  } catch (error) {
    console.error(error);
    return { sets: [] }; // Return an empty array in case of error
  }
});

// Async thunk to fetch words for each set
const fetchSetWords = createAsyncThunk("sets/fetchSetWords", async (sets) => {
  try {
    // Map over the sets array and execute the GraphQL query for each set
    const results = await Promise.all(
      sets.map(async (set) => {
        // Execute the GraphQL query to fetch words for each set
        const response = await client.graphql({
          query: wordsBySetName,
          variables: { setName: set.setName }, // Use setName from the current set
        });

        // Return an object with setName and the fetched words
        return {
          setName: set.setName,
          setImage: set.setImage,
          words: response.data.wordsBySetName?.items || [], // Ensure items is an array
        };
      })
    );

    // Return the array of results for each set
    return results;
  } catch (error) {
    console.error(error);
    throw new Error("Failed to fetch sets with words.");
  }
});

// Async thunk to generate signed URLs for images
const generateSetSignedUrls = createAsyncThunk(
  "sets/generateSetSignedUrls",
  async (sets) => {
    try {
      const setsSignedUrls = await Promise.all(
        sets.map(async (set) => {
          const response = await generateSignedURL(set.setImage, 600); // Generate signed URL with a 10-minute expiration
          const signedUrl = response.url.href;
          return { id: set.id, signedUrl };
        })
      );
      return setsSignedUrls;
    } catch (error) {
      console.error("Failed to generate signed URLs:", error);
      return [];
    }
  }
);

// Define the initial state
const initialState = {
  sets: [],
  status: "idle",
  error: null,
  setsSignedUrls: {}, // New field to store signed URLs
};

// Create a slice
const setsSlice = createSlice({
  name: "sets",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchSets.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchSets.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.sets = action.payload.sets; // Store the fetched sets in the state
      })
      .addCase(fetchSets.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message;
      })
      .addCase(fetchSetWords.fulfilled, (state, action) => {
        action.payload.forEach((setWithWords) => {
          const setIndex = state.sets.findIndex(
            (set) => set.setName === setWithWords.setName
          );
          if (setIndex !== -1) {
            state.sets[setIndex].words = setWithWords.words; // Add words to the corresponding set
          }
        });
      })
      .addCase(fetchSetWords.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message;
      })
      .addCase(generateSetSignedUrls.fulfilled, (state, action) => {
        action.payload.forEach(({ id, signedUrl }) => {
          state.setsSignedUrls[id] = signedUrl; // Store the signed URL in the state
        });
      });
  },
});

// Export the async thunks
export { fetchSets, fetchSetWords, generateSetSignedUrls };

// Export the reducer to configure in the store
export default setsSlice.reducer;
